// DocumentSelectorModal.js
import React, { useState, useEffect } from 'react';
import { Modal, Button, Spin, message, Empty, Pagination, Tabs, Input, Icon } from 'antd';
import axios from 'axios';
import { api } from '../../../../../../services';
import './DocumentSelectorModal.scss';
import { getToken } from '../../../../../../services/api/user';
import { config } from '../../../../../../constants';

const { TabPane } = Tabs;
const { Search } = Input;

const DocumentSelectorModal = ({ visible, onClose, onSelect, activeOrg }) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedDocs, setSelectedDocs] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(12);
  const [total, setTotal] = useState(0);
  const [activeTab, setActiveTab] = useState('my_media');
  const [viewMode, setViewMode] = useState('grid');
  const [searchQuery, setSearchQuery] = useState('');
  const [isRecommendationLoading, setIsRecommendationLoading] = useState(false);
  const [shlToken, setShlToken] = useState(null);

  const ACCEPTED_FORMATS = ['docx', 'doc', 'pdf', 'pptx', 'ppt', 'txt'];

  useEffect(() => {
    if (visible) {
      fetchDocuments(1, activeTab, searchQuery);
    } else {
      setCurrentPage(1);
      setSelectedDocs([]);
      setSearchQuery('');
      setIsRecommendationLoading(false);
    }
  }, [visible]);

  useEffect(() => {
    const fetchTokenAsync = async () => {
      try {
        const token = await getToken();
        setShlToken(token);
      } catch (error) {
        console.error('Error fetching token:', error);
      }
    };

    fetchTokenAsync();
  }, []);

  const getFileExtension = (filename) => {
    if (!filename) return 'file';
    const parts = filename.split('.');
    return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : 'file';
  };

  const formatFileType = (mimeType, filename) => {
    if (!mimeType) return getFileExtension(filename);

    if (mimeType.includes('pdf')) return 'pdf';
    if (mimeType.includes('word') || mimeType.includes('msword')) return 'docx';
    if (mimeType.includes('powerpoint') || mimeType.includes('presentation')) return 'pptx';
    if (mimeType.includes('text/plain')) return 'txt';
    if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) return 'xlsx';

    return getFileExtension(filename);
  };

  const isAcceptedFormat = (fileType) => {
    return ACCEPTED_FORMATS.includes(fileType.toLowerCase());
  };

  const trimFileName = (filename) => {
    if (!filename) return '';

    const maxLength = 25;
    const parts = filename.split('.');
    const extension = parts.length > 1 ? parts.pop() : '';
    const nameWithoutExt = parts.join('.');

    if (filename.length <= maxLength) {
      return filename;
    }

    const trimmedName = nameWithoutExt.substring(0, maxLength - extension.length - 3);
    return trimmedName + '...' + extension;
  };

  const fetchDocuments = async (page, category, search) => {
    setLoading(true);
    try {
      const searchValue = search || '';
      
      if (category === 'my_media') {
        const response = await api.library.getLibraryList({
          limit: 100,
          onlyMedia: true,
          offset: 0,
          search: searchValue,
          sort: 'NEWEST',
          type: '',
          isActiveOrg: true
        });

        console.log("response from library ", response);

        if (response && response.data && Array.isArray(response.data.rows)) {
          const transformedDocs = response.data.rows
            .filter(function(item) {
              return item.is_ai_enabled === true;
            })
            .map(function(item) {
              return {
                id: item.id,
                name: item.filename,
                type: formatFileType(item.media_mime_type, item.filename),
                category: 'my_media',
                owner: item.creator ? item.creator.name : 'Unknown',
                lastUpdated: item.updated_at,
                link: item.link,
                mimeType: item.media_mime_type
              };
            })
            .filter(function(doc) {
              return isAcceptedFormat(doc.type);
            });

          console.log("transformedDocs from my_media: ", transformedDocs);
          setDocuments(transformedDocs);
          setTotal(transformedDocs.length);
        } else {
          setDocuments([]);
          setTotal(0);
        }
      } else if (category === 'org_media') {
        const response = await api.library.getLibraryList({
          limit: 100,
          onlyMedia: true,
          offset: 0,
          search: searchValue,
          sort: 'NEWEST',
          type: '',
          isOrg: true
        });

        console.log("response from org library ", response);

        if (response && response.data && Array.isArray(response.data.rows)) {
          const transformedDocs = response.data.rows
            .filter(function(item) {
              return item.is_ai_enabled === true;
            })
            .map(function(item) {
              return {
                id: item.id,
                name: item.filename,
                type: formatFileType(item.media_mime_type, item.filename),
                category: 'org_media',
                owner: item.creator ? item.creator.name : 'Unknown',
                lastUpdated: item.updated_at,
                link: item.link,
                mimeType: item.media_mime_type
              };
            })
            .filter(function(doc) {
              return isAcceptedFormat(doc.type);
            });

          console.log("transformedDocs from org_media: ", transformedDocs);
          setDocuments(transformedDocs);
          setTotal(transformedDocs.length);
        } else {
          setDocuments([]);
          setTotal(0);
        }
      }
    } catch (error) {
      console.error('Error fetching documents:', error);
      message.error('Failed to load documents');
      setDocuments([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  const getFileIcon = (type) => {
    const iconStyle = { fontSize: '36px' };
    const fileType = type.toLowerCase();
    
    if (fileType === 'pdf') {
      return <Icon type="file-pdf" style={{ ...iconStyle, color: '#ff4d4f' }} />;
    } else if (fileType === 'doc' || fileType === 'docx') {
      return <Icon type="file-word" style={{ ...iconStyle, color: '#1890ff' }} />;
    } else if (fileType === 'ppt' || fileType === 'pptx') {
      return <Icon type="file-ppt" style={{ ...iconStyle, color: '#ff7a45' }} />;
    } else if (fileType === 'txt') {
      return <Icon type="file-text" style={{ ...iconStyle, color: '#52c41a' }} />;
    } else {
      return <Icon type="file-text" style={{ ...iconStyle, color: '#8c8c8c' }} />;
    }
  };

  const handleDocumentClick = (doc) => {
    const isSelected = selectedDocs.some(function(d) {
      return d.id === doc.id;
    });

    if (isSelected) {
      setSelectedDocs(selectedDocs.filter(function(d) {
        return d.id !== doc.id;
      }));
    } else {
      setSelectedDocs(selectedDocs.concat([doc]));
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchDocuments(page, activeTab, searchQuery);
  };

  const handleTabChange = (key) => {
    setActiveTab(key);
    setCurrentPage(1);
    setSearchQuery('');
    fetchDocuments(1, key, '');
  };

  const handleSearch = async (value) => {
    setSearchQuery(value);
    setCurrentPage(1);
    console.log("log value param", value);

    // If search is cleared, fetch all documents normally
    if (!value || value.length === 0) {
      fetchDocuments(1, activeTab, '');
      return;
    }

    // If search has value, use recommendation API
    if (value.length > 0 && !isRecommendationLoading) {
      setIsRecommendationLoading(true);
      setLoading(true);
      
      try {
        // First, fetch all documents for the current tab
        const libraryResponse = await api.library.getLibraryList({
          limit: 100,
          onlyMedia: true,
          offset: 0,
          search: '',
          sort: 'NEWEST',
          type: '',
          isActiveOrg: activeTab === 'my_media',
          isOrg: activeTab === 'org_media'
        });

        console.log('Library response:', libraryResponse);

        // Then call recommendation API
        const recommendResponse = await axios.post(
          config.aiContentGenerationUrl + 'recommend-filenames',
          {
            query: value,
            org_id: String(activeOrg || '94')
          },
          {
            headers: {
              'Authorization': 'Bearer ' + shlToken,
              'Content-Type': 'application/json'
            }
          }
        );

        console.log('Recommendation API response:', recommendResponse.data);

        if (
          recommendResponse.data.success &&
          recommendResponse.data.suggested_files &&
          recommendResponse.data.suggested_files.length > 0 &&
          libraryResponse &&
          libraryResponse.data &&
          Array.isArray(libraryResponse.data.rows)
        ) {
          const suggestedFiles = recommendResponse.data.suggested_files;

          // Filter and transform documents based on suggested files
          const matchedDocuments = libraryResponse.data.rows
            .filter(function(item) {
              if (!item.is_ai_enabled) return false;
              if (!item.link) return false;

              // Extract path from link (remove query parameters)
              const linkPath = item.link.split('?')[0];
              
              // Extract the relative path (everything after the domain)
              const urlParts = linkPath.split('.com/');
              const relativePath = urlParts.length > 1 ? urlParts[1] : '';

              // Check if this path matches any suggested file
              return suggestedFiles.some(function(suggestedFile) {
                return relativePath === suggestedFile || linkPath.indexOf(suggestedFile) !== -1;
              });
            })
            .map(function(item) {
              return {
                id: item.id,
                name: item.filename,
                type: formatFileType(item.media_mime_type, item.filename),
                category: activeTab,
                owner: item.creator ? item.creator.name : 'Unknown',
                lastUpdated: item.updated_at,
                link: item.link,
                mimeType: item.media_mime_type
              };
            })
            .filter(function(doc) {
              return isAcceptedFormat(doc.type);
            });

          console.log('Matched documents:', matchedDocuments);

          if (matchedDocuments.length > 0) {
            setDocuments(matchedDocuments);
            setTotal(matchedDocuments.length);
          } else {
            // No matches found, show empty results
            setDocuments([]);
            setTotal(0);
            message.info('No matching documents found for your search');
          }
        } else {
          // API call failed or no suggestions, fallback to normal search
          console.log('Falling back to normal search');
          fetchDocuments(1, activeTab, value);
        }
      } catch (error) {
        console.error('Error in search with recommendations:', error);
        // On error, fallback to normal search
        fetchDocuments(1, activeTab, value);
      } finally {
        setIsRecommendationLoading(false);
        setLoading(false);
      }
    }
  };

  const handleSelect = () => {
    if (selectedDocs.length > 0) {
      onSelect(selectedDocs);
      handleClose();
    } else {
      message.warning('Please select at least one document');
    }
  };

  const handleClose = () => {
    setSelectedDocs([]);
    setCurrentPage(1);
    setSearchQuery('');
    onClose();
  };

  const isDocSelected = (docId) => {
    return selectedDocs.some(function(d) {
      return d.id === docId;
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return diffDays + ' days ago';
    if (diffDays < 30) return Math.floor(diffDays / 7) + ' weeks ago';
    if (diffDays < 365) return Math.floor(diffDays / 30) + ' months ago';
    return date.toLocaleDateString();
  };

  const renderGridView = () => (
    <div className="documents-grid">
      {documents.map(function(doc) {
        return (
          <div
            key={doc.id}
            className={'document-tile' + (isDocSelected(doc.id) ? ' selected' : '')}
            onClick={function() { handleDocumentClick(doc); }}
          >
            {isDocSelected(doc.id) && (
              <div className="selected-badge">
                <Icon type="check-circle" />
              </div>
            )}
            <div className="document-icon">{getFileIcon(doc.type)}</div>
            <div className="document-name" title={doc.name}>
              {trimFileName(doc.name)}
            </div>
            <div className="document-meta">
              <div className="document-owner" title={doc.owner}>{doc.owner}</div>
              <div className="document-date">{formatDate(doc.lastUpdated)}</div>
            </div>
            <div className="document-type">{doc.type.toUpperCase()}</div>
          </div>
        );
      })}
    </div>
  );

  const renderListView = () => (
    <div className="documents-list">
      {documents.map(function(doc) {
        return (
          <div
            key={doc.id}
            className={'document-list-item' + (isDocSelected(doc.id) ? ' selected' : '')}
            onClick={function() { handleDocumentClick(doc); }}
          >
            <div className="list-item-icon">{getFileIcon(doc.type)}</div>
            <div className="list-item-details">
              <div className="list-item-name" title={doc.name}>{doc.name}</div>
              <div className="list-item-meta">
                <span className="list-item-owner">{doc.owner}</span>
                <span className="list-item-separator">•</span>
                <span className="list-item-date">{formatDate(doc.lastUpdated)}</span>
                <span className="list-item-separator">•</span>
                <span className="list-item-type">{doc.type.toUpperCase()}</span>
              </div>
            </div>
            {isDocSelected(doc.id) && (
              <Icon type="check-circle" className="list-item-check" style={{ fontSize: '20px', color: '#1890ff' }} />
            )}
          </div>
        );
      })}
    </div>
  );

  return (
    <Modal
      visible={visible}
      title={
        <div>
          Select Document(s)
          {selectedDocs.length > 0 && (
            <span style={{ marginLeft: '10px', color: '#1890ff', fontSize: '14px' }}>
              ({selectedDocs.length} selected)
            </span>
          )}
        </div>
      }
      onCancel={handleClose}
      width={1000}
      footer={[
        <Button key="close" onClick={handleClose}>
          Close
        </Button>,
        <Button
          key="select"
          type="primary"
          onClick={handleSelect}
          disabled={selectedDocs.length === 0}
        >
          Select ({selectedDocs.length})
        </Button>
      ]}
      className="document-selector-modal"
    >
      <div className="document-selector-content">
        <div className="controls-bar">
          <Search
            placeholder="Search documents..."
            allowClear
            value={searchQuery}
            onChange={function(e) { setSearchQuery(e.target.value); }}
            onSearch={handleSearch}
            style={{ width: 300, marginRight: 'auto' }}
          />
          <div className="view-toggle">
            <Button
              type={viewMode === 'grid' ? 'primary' : 'default'}
              icon="appstore"
              onClick={function() { setViewMode('grid'); }}
              title="Grid View"
            />
            <Button
              type={viewMode === 'list' ? 'primary' : 'default'}
              icon="unordered-list"
              onClick={function() { setViewMode('list'); }}
              style={{ marginLeft: '8px' }}
              title="List View"
            />
          </div>
        </div>

        <Tabs activeKey={activeTab} onChange={handleTabChange}>
          <TabPane tab="My Media" key="my_media">
            {loading ? (
              <div className="loading-container">
                <Spin size="large" tip="Loading documents..." />
              </div>
            ) : documents.length === 0 ? (
              <Empty description="No documents available" />
            ) : (
              <React.Fragment>
                {viewMode === 'grid' ? renderGridView() : renderListView()}

                {total > pageSize && (
                  <div className="pagination-container">
                    <Pagination
                      current={currentPage}
                      pageSize={pageSize}
                      total={total}
                      onChange={handlePageChange}
                      showSizeChanger={false}
                      showTotal={function(total, range) {
                        return range[0] + '-' + range[1] + ' of ' + total + ' documents';
                      }}
                    />
                  </div>
                )}
              </React.Fragment>
            )}
          </TabPane>

          <TabPane tab="Organization Library (SOP)" key="org_media">
            {loading || isRecommendationLoading ? (
              <div className="loading-container">
                <Spin size="large" tip="Loading documents..." />
              </div>
            ) : documents.length === 0 ? (
              <Empty description="No documents available" />
            ) : (
              <React.Fragment>
                {viewMode === 'grid' ? renderGridView() : renderListView()}

                {total > pageSize && (
                  <div className="pagination-container">
                    <Pagination
                      current={currentPage}
                      pageSize={pageSize}
                      total={total}
                      onChange={handlePageChange}
                      showSizeChanger={false}
                      showTotal={function(total, range) {
                        return range[0] + '-' + range[1] + ' of ' + total + ' documents';
                      }}
                    />
                  </div>
                )}
              </React.Fragment>
            )}
          </TabPane>
        </Tabs>
      </div>
    </Modal>
  );
};

export default DocumentSelectorModal;